import type { Pt } from "@tasduvar/engine";
import type { SheetPrim } from "./index";

/**
 * Pafta elle düzenleme çekirdeği (Cem 2026-09-29: "taşı, kopyala, ölçekle, yazı düzelt — AutoCAD'e ihtiyacımız kalmasın").
 * Üretilen her öğenin kararlı anahtarı `blok:tür:sıra`; parmak izi (`fp`) öğenin ilk noktası/yazısı — duvar değişip öğe
 * başkalaşırsa düzenleme sessizce uygulanmaz (yanlış öğeye binmez). Dönüşüm benzerliktir: p' = s·R(r)·p + t.
 */
/** Benzerlik dönüşümü: p' = s·R(r)·M·p + t; M = ayna (m: x eksenine göre yansıt) ya da birim */
export interface Tf { s: number; r: number; tx: number; ty: number; m?: boolean }
export interface Override { fp: string; del?: boolean; tf?: Tf; text?: string; h?: number; layer?: string; color?: number }
export interface SheetEdits { overrides: Record<string, Override>; added: Record<string, unknown>[]; exploded?: string[]; blocks?: Record<string, string[]> }
export const EMPTY_EDITS: SheetEdits = { overrides: {}, added: [], exploded: [], blocks: {} };
/** Eklenen öğenin kararlı anahtarı `u:<id>` (silmede diğerleri kaymaz); kimliksiz eski kayıtlar sıra numarasıyla */
export const addedKey = (a: Record<string, unknown>, i: number): string => `u:${typeof a.id === "string" ? a.id : i}`;
export const addedIndex = (added: Record<string, unknown>[], key: string): number => added.findIndex((a, i) => addedKey(a, i) === key);
export const newAddedId = (): string => Math.random().toString(36).slice(2, 10);

export const IDENTITY: Tf = { s: 1, r: 0, tx: 0, ty: 0 };
export const translate = (dx: number, dy: number): Tf => ({ s: 1, r: 0, tx: dx, ty: dy });
/** Temel nokta etrafında ölçek/döndürme */
export function about(base: Pt, s: number, rDeg: number): Tf {
  const c = Math.cos((rDeg * Math.PI) / 180) * s, n = Math.sin((rDeg * Math.PI) / 180) * s;
  return { s, r: rDeg, tx: base.x - (c * base.x - n * base.y), ty: base.y - (n * base.x + c * base.y) };
}
export function applyTf(t: Tf, p: Pt): Pt {
  const c = Math.cos((t.r * Math.PI) / 180) * t.s, n = Math.sin((t.r * Math.PI) / 180) * t.s;
  const y = t.m ? -p.y : p.y;
  return { x: c * p.x - n * y + t.tx, y: n * p.x + c * y + t.ty };
}
/** Önce `a`, sonra `b` (M·R(θ) = R(−θ)·M özdeşliğiyle kapalı biçim) */
export function compose(a: Tf, b: Tf): Tf {
  const o = applyTf(b, { x: a.tx, y: a.ty });
  const m = !!a.m !== !!b.m;
  return { s: a.s * b.s, r: b.r + (b.m ? -a.r : a.r), tx: o.x, ty: o.y, ...(m ? { m: true } : {}) };
}
/** p1–p2 doğrusuna göre ayna (AutoCAD MIRROR): R(2φ)·M, doğru üstündeki noktalar yerinde kalır */
export function mirrorAbout(p1: Pt, p2: Pt): Tf {
  const phi = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
  const t0: Tf = { s: 1, r: 2 * phi, tx: 0, ty: 0, m: true };
  const q = applyTf(t0, p1);
  return { ...t0, tx: p1.x - q.x, ty: p1.y - q.y };
}
/** Doğrultu açısının dönüşümü (derece) */
const mapAngle = (t: Tf, deg: number): number => t.r + (t.m ? -deg : deg);

/** Ölçü geometrisi (DXF dimLinear ile aynı): yön, ölçü çizgisi uçları, ölçülen uzunluk */
export function dimGeom(e: Extract<SheetPrim, { k: "dim" }>): { vertical: boolean; p1: Pt; p2: Pt; measure: number } {
  const vertical = e.dir ? e.dir === "v" : Math.abs(e.a.x - e.b.x) < 1e-9 && Math.abs(e.a.y - e.b.y) > 1e-9;
  const p1 = vertical ? { x: e.a.x + e.offset, y: e.a.y } : { x: e.a.x, y: e.a.y + e.offset };
  const p2 = vertical ? { x: e.a.x + e.offset, y: e.b.y } : { x: e.b.x, y: e.a.y + e.offset };
  return { vertical, p1, p2, measure: vertical ? Math.abs(e.b.y - e.a.y) : Math.abs(e.b.x - e.a.x) };
}

const r3 = (v: number): string => (Math.round(v * 1000) / 1000).toFixed(3);
export function fingerprint(e: SheetPrim): string {
  switch (e.k) {
    case "line": return `${r3(e.a.x)},${r3(e.a.y)}`;
    case "poly": case "solid": return `${r3(e.pts[0]?.x ?? 0)},${r3(e.pts[0]?.y ?? 0)}`;
    case "circle": return `${r3(e.c.x)},${r3(e.c.y)}`;
    case "text": return e.value;
    case "mtext": return e.lines[0] ?? "";
    case "hatch": return `${r3(e.loops[0]?.[0]?.x ?? 0)},${r3(e.loops[0]?.[0]?.y ?? 0)}`;
    case "dim": return `${r3(e.a.x)},${r3(e.a.y)}`;
  }
}
/** Blok içi tür sırasıyla kararlı anahtarlar */
export function assignKeys(prims: SheetPrim[]): SheetPrim[] {
  const n = new Map<string, number>();
  return prims.map((e) => { const b = `${e.g}:${e.k}`; const i = n.get(b) ?? 0; n.set(b, i + 1); return { ...e, key: `${b}:${i}`, fp: fingerprint(e) }; });
}

/** Öğeye benzerlik dönüşümü uygular (yazı yüksekliği/dönüşü, daire yarıçapı, ölçü ötelemesi dâhil). */
export function transformPrim(e: SheetPrim, t: Tf, label?: (e: Extract<SheetPrim, { k: "dim" }>) => string): SheetPrim {
  const P = (p: Pt): Pt => applyTf(t, p);
  switch (e.k) {
    case "line": return { ...e, a: P(e.a), b: P(e.b) };
    case "poly": return { ...e, pts: e.pts.map(P) };
    case "solid": return { ...e, pts: e.pts.map(P) };
    case "hatch": return { ...e, loops: e.loops.map((L) => L.map(P)) };
    case "circle": return { ...e, c: P(e.c), r: e.r * t.s };
    case "text": {
      // Aynada yazı ters çevrilmez (AutoCAD MIRRTEXT=0): okunur yöne döndürülür, yatay hizası değişir
      let rot = (((mapAngle(t, e.opts.rotation ?? 0)) % 360) + 360) % 360;
      let halign = e.opts.halign;
      // Okuma yönü tersine döndüyse 180° çevrilir; yazı artık noktanın öbür yanına uzanmalı → sol/sağ hiza yer değiştirir
      if (t.m && rot > 90 + 1e-6 && rot < 270 - 1e-6) {
        rot = (rot + 180) % 360;
        halign = halign === 0 || halign === undefined ? 2 : halign === 2 ? 0 : halign;
      }
      return { ...e, p: P(e.p), opts: { ...e.opts, h: e.opts.h * t.s, rotation: Math.round(rot * 1e6) / 1e6, halign } };
    }
    case "mtext": {
      // MTEXT sol üst köşeden akar: aynada yerel x/y ekseni ters dönerse yerleştirme köşesi karşı köşeye alınır (yazı ters çevrilmez)
      if (!t.m) return { ...e, p: P(e.p), h: e.h * t.s, width: e.width * t.s };
      const ax = (mapAngle(t, 0) * Math.PI) / 180, ay = (mapAngle(t, 90) * Math.PI) / 180;
      const corner = { x: e.p.x + (Math.cos(ax) < 0 ? e.width : 0), y: e.p.y - (Math.sin(ay) < 0 ? e.h * 1.5 * Math.max(1, e.lines.length) : 0) };
      return { ...e, p: P(corner), h: e.h * t.s, width: e.width * t.s };
    }
    case "dim": {
      // Ölçü yatay/düşey kalır: 90°'nin katı dışındaki döndürmede yalnız öteleme uygulanır; ölçekte metin eski ölçüde dondurulur.
      // Ölçü çizgisinin a'daki ucu da dönüştürülür, yeni öteleme ondan okunur (90/270°'de eksen değişir).
      const quarter = Math.abs(((t.r % 90) + 90) % 90) < 1e-6;
      const tt = quarter ? t : { s: t.s, r: 0, tx: t.tx, ty: t.ty };
      const text = e.text ?? (Math.abs(t.s - 1) > 1e-9 && label ? label(e) : undefined);
      const g0 = dimGeom(e);
      const a = applyTf(tt, e.a), b = applyTf(tt, e.b), p1 = applyTf(tt, g0.p1);
      const odd = Math.round((((tt.r % 360) + 360) % 360) / 90) % 2 === 1;   // ayna eksenleri korur, yalnız döndürme eksen değiştirir
      const vertical = g0.vertical !== odd;
      return { ...e, a, b, offset: vertical ? p1.x - a.x : p1.y - a.y, text, dir: vertical ? "v" : "h" };
    }
  }
}

/** Üretilen öğelere düzenlemeleri uygular, eklenen öğeleri sona koyar. */
export function applyEdits(prims: SheetPrim[], edits: SheetEdits | undefined, label: (e: Extract<SheetPrim, { k: "dim" }>) => string): SheetPrim[] {
  if (!edits) return prims;
  const out: SheetPrim[] = [];
  for (const e of prims) {
    const o = e.key ? edits.overrides[e.key] : undefined;
    if (!o || o.fp !== e.fp) { out.push(e); continue; }
    if (o.del) continue;
    let x: SheetPrim = e;
    if (o.text !== undefined) {
      if (x.k === "text") x = { ...x, value: o.text };
      else if (x.k === "mtext") x = { ...x, lines: o.text.split("\n") };
      else if (x.k === "dim") x = { ...x, text: o.text };
    }
    if (o.h !== undefined) { if (x.k === "text") x = { ...x, opts: { ...x.opts, h: o.h } }; else if (x.k === "mtext") x = { ...x, h: o.h }; }
    if (o.layer !== undefined || o.color !== undefined) x = restyle(x, o.layer, o.color);
    if (o.tf) x = transformPrim(x, o.tf, label);
    out.push(x);
  }
  edits.added.forEach((a, i) => { const p = a as unknown as SheetPrim; if (p && typeof p === "object" && "k" in p) out.push({ ...p, g: "user", key: addedKey(a, i) }); });
  return out;
}

/**
 * Leader yazısı yer değiştirdiyse (elle taşıma ya da otomatik çakışma giderme) ok ucu sabit kalır, çizgi yazıyı izler: kolun dirsek
 * ucu ve sahanlık yazının yer değiştirmesi kadar ötelenir (Cem: "yazı taşınınca çizgi uzasın"). Kendisi elle dönüştürülmüş çizgiye
 * (yazıyla birlikte seçilip taşınmış) dokunulmaz. `ref`: düzenleme ve çakışma gidermeden önceki öğeler (aynı anahtarlar).
 */
export function followLeaders(ref: SheetPrim[], out: SheetPrim[], edits: SheetEdits | undefined): SheetPrim[] {
  const before = new Map(ref.filter((e) => e.lead?.role === "text").map((e) => [e.lead!.id, e]));
  const delta = new Map<string, Pt>();
  for (const e of out) {
    if (e.lead?.role !== "text" || e.k !== "text") continue;
    const b = before.get(e.lead.id);
    if (b?.k !== "text") continue;
    const d = { x: e.p.x - b.p.x, y: e.p.y - b.p.y };
    if (Math.hypot(d.x, d.y) > 1e-9) delta.set(e.lead.id, d);
  }
  if (!delta.size) return out;
  const own = (e: SheetPrim): boolean => { const o = e.key ? edits?.overrides[e.key] : undefined; return !!o && o.fp === e.fp && !!o.tf; };
  return out.map((e) => {
    const d = e.lead ? delta.get(e.lead.id) : undefined;
    if (!d || e.k !== "line" || own(e)) return e;
    const P = (p: Pt): Pt => ({ x: p.x + d.x, y: p.y + d.y });
    return e.lead!.role === "arm" ? { ...e, b: P(e.b) } : e.lead!.role === "land" ? { ...e, a: P(e.a), b: P(e.b) } : e;
  });
}

// ── Seçim ve yakalama ─────────────────────────────────────────────────────────
export interface Box { minX: number; minY: number; maxX: number; maxY: number }
const segDist = (p: Pt, a: Pt, b: Pt): number => {
  const dx = b.x - a.x, dy = b.y - a.y, L = dx * dx + dy * dy;
  const t = L ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};
const textW = (s: string, h: number): number => s.replace(/%%U/g, "").length * h * 0.6;

/** Öğe kutusu (seçim ve pencere seçimi için; yazıda dönüş hesaba katılır) */
export function primBox(e: SheetPrim): Box {
  const b: Box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const add = (p: Pt): void => { b.minX = Math.min(b.minX, p.x); b.minY = Math.min(b.minY, p.y); b.maxX = Math.max(b.maxX, p.x); b.maxY = Math.max(b.maxY, p.y); };
  switch (e.k) {
    case "line": add(e.a); add(e.b); break;
    case "poly": case "solid": e.pts.forEach(add); break;
    case "hatch": e.loops.forEach((L) => L.forEach(add)); break;
    case "circle": add({ x: e.c.x - e.r, y: e.c.y - e.r }); add({ x: e.c.x + e.r, y: e.c.y + e.r }); break;
    case "text": {
      const w = textW(e.value, e.opts.h), ha = e.opts.halign ?? 0, x0 = ha === 1 ? -w / 2 : ha === 2 ? -w : 0;
      const t = { s: 1, r: e.opts.rotation ?? 0, tx: e.p.x, ty: e.p.y };
      for (const c of [{ x: x0, y: -e.opts.h * 0.25 }, { x: x0 + w, y: -e.opts.h * 0.25 }, { x: x0 + w, y: e.opts.h }, { x: x0, y: e.opts.h }]) add(applyTf(t, c));
      break;
    }
    case "mtext": add(e.p); add({ x: e.p.x + e.width, y: e.p.y - e.h * 1.5 * Math.max(1, e.lines.length) }); break;
    case "dim": { const d = dimGeom(e); add(e.a); add(e.b); add(d.p1); add(d.p2); break; }
  }
  return b;
}

/** Noktaya en yakın öğe (tolerans m); yazı/doldurma kutu içindeyse isabet. Önce çizgi türleri (üstte olanlar) tercih edilir. */
export function hitTest(prims: SheetPrim[], p: Pt, tol: number): SheetPrim | null {
  let best: SheetPrim | null = null, bestD = tol;
  for (let i = prims.length - 1; i >= 0; i--) {
    const e = prims[i]!;
    let d = Infinity;
    switch (e.k) {
      case "line": d = segDist(p, e.a, e.b); break;
      case "poly": for (let j = 1; j < e.pts.length + (e.closed ? 1 : 0); j++) d = Math.min(d, segDist(p, e.pts[j - 1]!, e.pts[j % e.pts.length]!)); break;
      case "circle": d = Math.abs(Math.hypot(p.x - e.c.x, p.y - e.c.y) - e.r); break;
      case "text": case "mtext": case "dim": case "solid": { const b = primBox(e); d = p.x >= b.minX - tol && p.x <= b.maxX + tol && p.y >= b.minY - tol && p.y <= b.maxY + tol ? tol * 0.5 : Infinity; break; }
      case "hatch": { const b = primBox(e); d = p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY ? tol * 0.9 : Infinity; break; }
    }
    if (d < bestD) { bestD = d; best = e; }
  }
  return best;
}

/** Pencere (soldan sağa: tamamen içindekiler) ya da kesişen (sağdan sola: kutusu değenler) seçim */
export function boxSelect(prims: SheetPrim[], a: Pt, b: Pt): SheetPrim[] {
  const w = { minX: Math.min(a.x, b.x), maxX: Math.max(a.x, b.x), minY: Math.min(a.y, b.y), maxY: Math.max(a.y, b.y) };
  const crossing = b.x < a.x;
  return prims.filter((e) => {
    const q = primBox(e);
    return crossing ? q.maxX >= w.minX && q.minX <= w.maxX && q.maxY >= w.minY && q.minY <= w.maxY : q.minX >= w.minX && q.maxX <= w.maxX && q.minY >= w.minY && q.maxY <= w.maxY;
  });
}

/** Uç nokta / merkez / yazı noktası yakalama (OSNAP) */
export function snapPoint(prims: SheetPrim[], p: Pt, tol: number): Pt | null {
  let best: Pt | null = null, bd = tol;
  const test = (q: Pt): void => { const d = Math.hypot(q.x - p.x, q.y - p.y); if (d < bd) { bd = d; best = q; } };
  for (const e of prims) {
    if (e.k === "line") { test(e.a); test(e.b); test({ x: (e.a.x + e.b.x) / 2, y: (e.a.y + e.b.y) / 2 }); }
    else if (e.k === "poly" || e.k === "solid") e.pts.forEach(test);
    else if (e.k === "circle") test(e.c);
    else if (e.k === "text" || e.k === "mtext") test(e.p);
    else if (e.k === "dim") { test(e.a); test(e.b); }
  }
  return best;
}

/** Katman / renk değiştir (renk: ACI; 0 = katmandan). Tarama katmanı sabit (TARAMA), yalnız rengi değişir. */
export function restyle(e: SheetPrim, layer?: string, color?: number): SheetPrim {
  const c = color === 0 ? undefined : color;
  switch (e.k) {
    case "line": case "poly": return { ...e, ...(layer ? { layer } : {}), ...(color !== undefined ? { extra: { ...e.extra, color: c } } : {}) };
    case "text": return { ...e, ...(layer ? { layer } : {}), ...(color !== undefined ? { opts: { ...e.opts, color: c } } : {}) };
    case "hatch": return color !== undefined ? { ...e, color: c } : e;
    case "circle": case "mtext": case "solid": return { ...e, ...(layer ? { layer } : {}) };
    case "dim": return { ...e, ...(layer ? { layer } : {}) };
  }
}
